/**
 * Read-adapter from the real audit-log wire shape to the UI's
 * `PayRunAuditEntry[]` (C4c, #21956; on-demand real fetch added C10,
 * #22332 — see this file's "On-demand real fetch + screen state resolution"
 * section below for why the real source is `audit.list`, not
 * `audit.listByDocument`). Pure, no React — mirrors
 * `wagepro_store_pay_runs.ts`'s `derivePayRunsFromRecords` pattern exactly:
 * `RealAuditEntryRow` is the permanent adapter shape (survives the #21929
 * employer-admin OAuth scope work unchanged; only HOW rows get resolved
 * changes), and `deriveAuditEntriesFromRows` is the permanent seam
 * `use_pay_run_audit.ts` (the on-demand hook) calls. Historically,
 * `wagepro_store_pay_runs.ts`'s `derivePayRunsFromRecords` called it too via
 * a boot-time fan-out (`auditRowsByRunId`) — removed entirely (not merely
 * left dead) by a follow-up adversarial-review pass on C10 (#22378); see
 * that module's docblock.
 *
 * ── Why only two of the seven mock audit-action kinds ever appear ─────────
 *
 * `PayRunAuditAction` (`types.ts`) has 7 members, but the real tenant
 * `audit_log` only ever records TWO of them against `entity_type = 'pay_run'`
 * with a real `logAuditEntry` call reachable from THIS epic's scope:
 *
 * - `payroll.pay_run.create` -> `run_created`
 * - `payroll.pay_run.approve` -> `approved`
 *
 * The other five kinds have no real audit source at all (documented gaps,
 * not adapter bugs):
 *
 * - `exceptions_acknowledged` — acknowledgement is deliberately client-local
 *   and never persisted server-side (see `wagepro_store_pay_runs.ts`'s
 *   `deriveExceptions` docblock); there is no server-side event to read.
 * - `submitted_for_approval` — no distinct "submitted for review" step
 *   exists server-side (a draft run is implicitly ready for review the
 *   moment it exists).
 * - `paid` — no `markPayRunPaid`-style procedure exists in
 *   `apps/api/src/trpc/routers/payroll.ts` today; `pay_runs.status` can read
 *   `'paid'` but nothing in this epic's scope writes it.
 * - `bank_file_generated` — `getSalaryDisbursementFile` is a download-only
 *   `.query` (never a `.mutation`), so it never calls `logAuditEntry`; there
 *   is no persisted event for "a disbursement file was generated".
 * - `statutory_filing_submitted` — `filingCalendar.markFiled` is a
 *   status-flag mutation on the entity-wide filing calendar (not scoped to
 *   a `pay_run_id`) and does not call `logAuditEntry` either; even if it
 *   did, it would not be reachable via `document_type: 'pay_run'`.
 *
 * `payroll.pay_run.void` / `payroll.pay_run.delete` (real entity_type
 * `'pay_run'` audit rows, from `voidPayRun`/`deletePayRun`) ARE real events
 * this adapter could see, but have no `PayRunAuditAction` kind to map onto
 * — `voidPayRun`/`deletePayRun` UI is explicitly out of scope for this whole
 * epic (flagged as a gap, never built), so the mock type was never extended
 * with a matching kind. `deriveAuditEntriesFromRows` drops any action string
 * it does not recognise (a documented catch-all, not a bug) rather than
 * guessing a kind or throwing.
 *
 * `payroll.statutory_payment.record` / `.void` ARE real events this pass
 * introduces UI for, but they are logged with `entity_type: 'statutory_payment'`
 * and `entity_ref_id: <payment_id>`, not `entity_type: 'pay_run'` — a
 * `document_type: 'pay_run'` query never returns them. They are visible
 * instead via the dedicated statutory-payments panel this pass adds to
 * `PostRunStep` (`payroll.listStatutoryPayments`), not the audit drawer.
 *
 * ── PII safety ──────────────────────────────────────────────────────────
 *
 * `RealAuditEntryRow` below is an intentional subset of
 * `audit.listByDocument`'s already-PII-scrubbed `auditEntryProjection`
 * (`apps/api/src/trpc/routers/audit.ts` — no IP/geo/device/session token is
 * ever selected there in the first place). This adapter narrows further
 * still: it reads only `id` / `action` / `actor_id` / `actor_display_name` /
 * `created_at` and NEVER reads or forwards `changes` (arbitrary
 * caller-supplied JSON that can carry incidental PII, e.g. a bank account
 * number on a `statutory_payment.record` entry) or `client_id` /
 * `auth_strength` (non-repudiation attribution, out of scope for this
 * lightweight drawer).
 */
import type { PayRunAuditAction, PayRunAuditEntry } from './types';

/**
 * One `audit.listByDocument` row, narrowed to exactly the fields this
 * adapter reads — same "intentional subset, not a mirror" stance as
 * `RealPayRunLine` (`wagepro_store_pay_runs.ts`). See module docblock's
 * "PII safety" section for why `changes` / `client_id` / `auth_strength`
 * are deliberately absent from this type.
 */
export interface RealAuditEntryRow {
  /** `audit_log.id` is an auto-incrementing SQLite `INTEGER` primary key, not a ULID — unlike almost every other id in this codebase. Converted to a string in {@link deriveAuditEntriesFromRows} to match `PayRunAuditEntry.id`. */
  id: number;
  /** Free-text wire action string, e.g. `'payroll.pay_run.create'` — narrowed by {@link ACTION_TO_AUDIT_KIND}. */
  action: string;
  actor_id: string | null;
  /**
   * Server-resolved display name (full name, falling back to email), when
   * known. An optional property (not `| null`) because its TWO real
   * sources disagree on how "unknown" is spelled on the wire:
   * `audit.listByDocument` pre-resolves a null actor straight to the
   * literal string `'System'` (a `Map.get()` lookup seeded with `null ->
   * 'System'`, which TypeScript types as possibly-absent rather than
   * possibly-`null`); `audit.list` (the actual source `fetchPayRunAuditRows`
   * uses — see this file's "On-demand real fetch" section for why) returns
   * a bare `null` instead and leaves the 'System' fallback to the CALLER
   * (`fetchPayRunAuditRows` narrows that `null` to `undefined` here). Either
   * way, {@link toAuditEntry}'s `row.actor_display_name ?? row.actor_id ??
   * 'System'` chain lands on the same `'System'` string for a genuinely
   * system-originated (null actor) event — just via a different mechanism
   * depending on which real procedure produced the row.
   */
  actor_display_name?: string;
  created_at: string;
}

/**
 * The only two real `audit_log.action` values this adapter recognises — see
 * module docblock for why the other five mock `PayRunAuditAction` kinds
 * never appear here.
 */
const ACTION_TO_AUDIT_KIND: Readonly<
  Partial<
    Record<
      string,
      Extract<PayRunAuditAction, { kind: 'run_created' | 'approved' }>['kind']
    >
  >
> = {
  'payroll.pay_run.create': 'run_created',
  'payroll.pay_run.approve': 'approved',
};

function toAuditEntry(row: RealAuditEntryRow): PayRunAuditEntry | null {
  const kind = ACTION_TO_AUDIT_KIND[row.action];
  if (kind === undefined) return null;
  return {
    id: String(row.id),
    action: { kind },
    actor: row.actor_display_name ?? row.actor_id ?? 'System',
    at: row.created_at,
  };
}

/**
 * Derives one pay run's audit entries from its real `audit.listByDocument`
 * rows, or passes `mock` through UNCHANGED when `rows` is `undefined` —
 * mirrors `derivePayRunsFromRecords`'s "real undefined -> mock unchanged"
 * contract exactly, at the per-run granularity `wagepro_store_pay_runs.ts`
 * needs (a real pay run's `mock` argument is always `[]`; there is no
 * meaningful "mock audit trail" for a run whose id never matches any mock
 * run's id — see that module's `derivePayRunsFromRecords`).
 *
 * An explicitly EMPTY `rows` array (a real run with zero recognised audit
 * rows: brand new, or every row's action fell through
 * {@link ACTION_TO_AUDIT_KIND}) is a real, distinct state from "not
 * resolved" — it renders an empty audit drawer, it does NOT fall back to
 * `mock`.
 *
 * Rows whose `action` is not one of the two recognised kinds are dropped
 * silently (not thrown, not surfaced as an "unknown event" placeholder) —
 * documented, not a bug; see module docblock.
 */
export function deriveAuditEntriesFromRows(
  mock: PayRunAuditEntry[],
  rows: readonly RealAuditEntryRow[] | undefined,
): PayRunAuditEntry[] {
  if (rows === undefined) return mock;
  const entries: PayRunAuditEntry[] = [];
  for (const row of rows) {
    const entry = toAuditEntry(row);
    if (entry !== null) entries.push(entry);
  }
  return entries;
}

/** The real `audit_log.action` `approvePayRun` writes for a pay run. */
const PAY_RUN_APPROVE_ACTION = 'payroll.pay_run.approve';

/**
 * The approver's server-resolved display name for one pay run, read from its
 * `payroll.pay_run.approve` audit row(s) — the newest one wins if a run was
 * somehow approved more than once. Only `actor_display_name` is trusted:
 * unlike {@link toAuditEntry} this never falls back to `actor_id` (a raw
 * user id is not a name a person should see) or to `'System'`. `undefined`
 * when the rows are unresolved, hold no approve entry, or the name is blank
 * — the caller then omits the name rather than printing a placeholder.
 */
export function approverDisplayNameFromRows(
  rows: readonly RealAuditEntryRow[] | undefined,
): string | undefined {
  if (rows === undefined) return undefined;
  let newest: RealAuditEntryRow | undefined;
  for (const row of rows) {
    if (row.action !== PAY_RUN_APPROVE_ACTION) continue;
    if (newest === undefined || row.created_at > newest.created_at) {
      newest = row;
    }
  }
  const name = newest?.actor_display_name?.trim();
  return name === undefined || name === '' ? undefined : name;
}

// ── On-demand real fetch + screen state resolution (C10, #22332) ───────────
//
// `use_pay_run_audit.ts` is the only caller of everything below — mirrors
// `wagepro_store_my_pay.ts`'s "pure decision function + thin hook" split
// (the state resolver AND the fetch/classification logic live here, fully
// unit-testable without React; the hook itself is a thin `useEffect` wrapper).
//
// ── Why `audit.list`, not `audit.listByDocument` (deviation from the issue
//    text) ──────────────────────────────────────────────────────────────────
//
// #22332's issue text says "real `audit.listByDocument` via the bearer
// client". That procedure is NOT reachable by a WagePro OAuth token today:
// `apps/api/src/trpc/oauth_allowlist.ts` maps `audit.listByDocument` to
// `['audit:read']` only — a scope WagePro's client never requests (and
// deliberately does not: see that allowlist entry's own inline comment,
// "#21929 PR 2 adversarial-review fix (MEDIUM-2)", for the two independent
// reasons widening it to `payroll:employer:*` was proposed and REJECTED on
// review — it would silently also widen `public.v1.audit.listByDocument`'s
// third-party partner surface, and it is the one PII-bearing,
// audience-exempt MCP path in that set). That same commit explicitly says
// "`audit.list` already serves WagePro's audit drawer" and allowlists
// `audit.list` for `payroll:employer:read`/`payroll:employer:write` instead
// — confirmed still true on this epic branch (`audit.listByDocument` has
// never been widened here; a later attempt to do so was not merged). This
// module therefore fetches via `audit.list` (entity-wide, paginated) and
// filters client-side for `entity_type === 'pay_run' && entity_ref_id ===
// <pay_run_id>` — the ONLY real, security-reviewed path WagePro's token can
// use. `RealAuditEntryRow`'s shape (the adapter contract `derivePayRunsFromRecords`
// and this module's own `deriveAuditEntriesFromRows` were built against) is
// unaffected: `audit.list`'s entries are a strict superset of
// `RealAuditEntryRow`'s fields (`entity_type`/`entity_ref_id` extra, used
// only for the client-side filter below, then discarded).
//
// ── Why `entitlement_required` is modeled but not reachable today ─────────
//
// `audit.listByDocument` calls `assertFeatureEnabled(cpDb, ctx.entityId,
// 'history_and_notes')` (`apps/api/src/trpc/routers/audit.ts`); `audit.list`
// does NOT — it has no plan-tier gate at all today, only the OAuth
// scope/audience/entity-access guards every `entityProcedure` already
// carries. So a real `audit.list` call can never actually reject with
// `data.requiredTier` set. `classifyAuditListFailure` still checks for it
// (rather than a bare `data.code === 'FORBIDDEN'` check) for two reasons:
// (1) it is the correct, non-bare-check classification discipline this
// epic's `isAal2Required`/`isStepUpRequired` (`lib/auth/step_up.ts`) already
// established — a bare FORBIDDEN check cannot tell a missing scope, a
// null-audience token, a failed IDOR check, an inactive subscription, and a
// genuine plan-gate rejection apart, and only the last of those is
// "entitlement required" (mirrors this epic's classification of
// `payroll.listPayRuns`'s FORBIDDEN — the missing-payroll-add-on case is the
// only one that maps to an entitlement-required-style state; every other
// FORBIDDEN, including an inactive subscription, resolves to a generic
// error); (2) if `audit.list` is ever given a plan gate in future (the
// entity-wide audit view is a plausible candidate for one, symmetric with
// `audit.listByDocument`'s), this classifier already handles it correctly
// with no frontend follow-up.
export type PayRunAuditLoadFailure = 'entitlement_required' | 'error';

/**
 * `empty_or_incomplete` (adversarial-review follow-up to C10, #22378) is
 * deliberately DISTINCT from `empty`: `empty` means the search covered the
 * entire relevant window and genuinely found nothing; `empty_or_incomplete`
 * means the search hit {@link AUDIT_LIST_MAX_PAGES}'s hard page cap before
 * either finding this pay run's entries OR being able to rule out (via the
 * `created_at` early-exit — see {@link fetchPayRunAuditRows}) that they
 * exist further back. Collapsing the two into one `empty` state would
 * repeat the exact bug this whole feature exists to fix (an affirmatively
 * false "no events" claim), just relocated from "fetch failed" to "search
 * truncated".
 */
export type PayRunAuditState =
  | { kind: 'loading' }
  | { kind: 'no_entity' }
  | { kind: 'entitlement_required' }
  | { kind: 'error' }
  | { kind: 'empty' }
  | { kind: 'empty_or_incomplete' }
  | { kind: 'ready'; entries: PayRunAuditEntry[] };

/**
 * Pure state resolver for the audit drawer — no network, no React.
 * `use_pay_run_audit.ts` is the only caller.
 *
 * Precedence:
 *   1. `no_entity` outranks everything — mirrors `resolveSessionFactsState`'s
 *      "a caller whose grant just lost its entity binding must see
 *      `no_entity`, not a leftover state from before".
 *   2. `payRunId === null` (the drawer isn't open for any run) -> `loading`,
 *      never a stale previous run's state — the hook clears `rows`/`failure`
 *      synchronously whenever `payRunId` changes (see that hook), so this
 *      branch is a defensive floor, not the primary mechanism.
 *   3. `entitlement_required` / `error` on the load.
 *   4. `rows` not yet resolved -> `loading`.
 *   5. Zero RECOGNISED entries (after `deriveAuditEntriesFromRows`'s action-
 *      kind filtering — see that function's docblock):
 *      - `truncated` true -> `empty_or_incomplete` (see that state's doc
 *        comment above) — the search hit the page cap without being able to
 *        confirm this run truly has no audit entries.
 *      - `truncated` false -> `empty`. A brand-new pay run, or one whose
 *        only real audit rows are action kinds this adapter doesn't
 *        recognise, and the search covered the entire relevant window — a
 *        real, distinct state from `error`, not a failure.
 *   6. Otherwise -> `ready`.
 */
export function resolvePayRunAuditState(input: {
  entityContextStatus: 'ready' | 'no_entity';
  payRunId: string | null;
  rows: readonly RealAuditEntryRow[] | undefined;
  failure: PayRunAuditLoadFailure | undefined;
  /** Whether {@link fetchPayRunAuditRows} hit its page cap before it could either find this run's entries or rule out (via the early-exit) that any exist. Defaults to `false` (the pre-#22378 behaviour) so existing callers/tests that never pass it keep resolving `empty` rather than the new state. */
  truncated?: boolean;
}): PayRunAuditState {
  if (input.entityContextStatus === 'no_entity') return { kind: 'no_entity' };
  if (input.payRunId === null) return { kind: 'loading' };
  if (input.failure === 'entitlement_required') {
    return { kind: 'entitlement_required' };
  }
  if (input.failure === 'error') return { kind: 'error' };
  if (input.rows === undefined) return { kind: 'loading' };

  const entries = deriveAuditEntriesFromRows([], input.rows);
  if (entries.length === 0) {
    return input.truncated === true
      ? { kind: 'empty_or_incomplete' }
      : { kind: 'empty' };
  }
  return { kind: 'ready', entries };
}

/** Machine-readable fields lifted off a caught tRPC error's `.data` — duplicated literal (not imported from `apps/api`, per docs/architecture-boundaries.md) mirroring `lib/auth/step_up.ts`'s `TrpcErrorDataLike` pattern. Source of truth: `apps/api/src/trpc/init.ts`'s `errorFormatter` and `apps/api/src/lib/plan_gate.ts`'s gate error shapes. */
interface TrpcErrorDataLike {
  code?: string | null;
  requiredTier?: string | null;
  subscriptionRequired?: boolean | null;
}

function hasTrpcErrorData(value: unknown): value is { data: unknown } {
  return typeof value === 'object' && value !== null && 'data' in value;
}

function isTrpcErrorDataLike(value: unknown): value is TrpcErrorDataLike {
  return typeof value === 'object' && value !== null;
}

/**
 * Classifies a caught `audit.list` rejection into the two `PayRunAuditLoadFailure`
 * buckets. See this section's module-level docblock ("Why entitlement_required
 * is modeled but not reachable today") for why this checks the structured
 * `data.requiredTier` field rather than a bare `data.code === 'FORBIDDEN'`
 * check, and why `data.subscriptionRequired` is deliberately excluded from
 * `entitlement_required` (an inactive subscription is a different, more
 * severe problem than a missing feature tier).
 */
export function classifyAuditListFailure(err: unknown): PayRunAuditLoadFailure {
  if (!hasTrpcErrorData(err)) return 'error';
  const data = err.data;
  if (!isTrpcErrorDataLike(data)) return 'error';
  if (data.code !== 'FORBIDDEN') return 'error';
  if (data.subscriptionRequired === true) return 'error';
  if (typeof data.requiredTier === 'string' && data.requiredTier.length > 0) {
    return 'entitlement_required';
  }
  return 'error';
}

/** `audit.list`'s max `limit` (see `apps/api/src/trpc/routers/audit.ts`'s `z.number().int().min(1).max(200)`). */
const AUDIT_LIST_PAGE_SIZE = 200;

/**
 * Bounds how many `audit.list` pages one drawer-open will fetch while
 * searching for a specific pay run's entries. `audit.list` is entity-WIDE
 * (every document type, not just `pay_run` — see module docblock above), so
 * an entity with heavy invoice/bill/contact audit activity could in
 * principle require many pages before reaching an older pay run's two
 * events. Unbounded pagination would make one drawer-open arbitrarily
 * expensive, so this caps worst-case cost at 5 requests (1000 entries).
 *
 * Adversarial review of C10 (#22378) found that hitting this cap while
 * `hasMore` was still true and silently returning zero matches produced an
 * affirmatively FALSE "no audit events" claim for any pay run whose real
 * entries were pushed past the window by unrelated (non-payroll) audit
 * volume on the same entity — exactly the empty-vs-failure ambiguity this
 * whole feature exists to eliminate, just relocated to a different axis.
 * Two changes fix this:
 *
 *  1. {@link fetchPayRunAuditRows} takes the target pay run's own
 *     `created_at` and stops paging (WITHOUT setting `truncated`) the
 *     moment a page's oldest entry predates it — since `audit.list` pages
 *     newest-first, nothing relevant to this run can exist further back,
 *     so the cap essentially never bites for a run whose creation predates
 *     the entity's recent unrelated audit volume, which is the overwhelming
 *     majority of real pay runs. It only genuinely bites when an entity has
 *     produced 1000+ audit rows of OTHER documents within the same time
 *     window as this run's own audit activity.
 *  2. When the cap genuinely IS hit (the early exit above never fired and
 *     `hasMore` is still true), `fetchPayRunAuditRows` reports
 *     `truncated: true` instead of silently returning `[]` — the caller
 *     (`resolvePayRunAuditState`) surfaces this as `empty_or_incomplete`,
 *     never `empty`.
 *
 * The immutable `audit_log` itself remains the actual compliance record via
 * other paths (see docs/security-rules.md); this drawer is a convenience
 * view, not the source of truth. Still flagged as the "wrong seam"
 * (filtering an entity-wide, offset-paginated feed client-side for one
 * document's rows) — tracked as issue #22377 for a narrow, document-scoped,
 * cursor-stable `payroll.getPayRunAuditTrail`-style procedure that would
 * remove this limitation entirely; not fixed here because it is a backend +
 * OAuth-allowlist change requiring its own pre-implementation Fable/Opus
 * plan pass per CLAUDE.md.
 */
const AUDIT_LIST_MAX_PAGES = 5;

/** One `audit.list` row, narrowed to exactly the fields this fetcher reads — a strict superset of {@link RealAuditEntryRow} (adds the two fields needed for the client-side per-document filter, `entity_type`/`entity_ref_id`, which are discarded before the result is narrowed to `RealAuditEntryRow[]`). */
export interface AuditListEntryWire {
  id: number;
  action: string;
  entity_type: string;
  entity_ref_id: string | null;
  actor_id: string | null;
  actor_display_name: string | null;
  created_at: string;
}

/** Injectable dependency so {@link fetchPayRunAuditRows} is unit-testable without a real tRPC client (mirrors this codebase's dependency-injection convention over module mocking). */
export interface AuditListDeps {
  listAudit: (input: {
    firm_id: string;
    entity_id: string;
    limit: number;
    offset: number;
  }) => Promise<{ entries: AuditListEntryWire[]; hasMore: boolean }>;
}

/** {@link fetchPayRunAuditRows}'s result — see that function's doc comment for what `truncated` means and why it exists (adversarial-review follow-up to C10, #22378). */
export interface FetchPayRunAuditRowsResult {
  rows: RealAuditEntryRow[];
  /**
   * `true` when the search hit {@link AUDIT_LIST_MAX_PAGES} while
   * `audit.list` still reported `hasMore: true` AND the `created_at`
   * early-exit (see {@link AUDIT_LIST_MAX_PAGES}'s doc comment) never
   * fired — i.e. the search genuinely could not confirm whether more of
   * this pay run's entries (or the absence of any) lie beyond the window
   * searched. `false` whenever the search either exhausted `audit.list`
   * (`hasMore: false`) or safely proved no earlier page could matter (the
   * early exit). Callers MUST treat `rows.length === 0 && truncated` as
   * "unknown", never as "empty" — see `resolvePayRunAuditState`'s
   * `empty_or_incomplete` state.
   */
  truncated: boolean;
}

/**
 * Fetches `audit.list` pages (bounded by {@link AUDIT_LIST_MAX_PAGES}) and
 * returns only the rows matching `(documentType, documentId)`, narrowed to
 * {@link RealAuditEntryRow}, sorted OLDEST-first by `id` (`audit_log.id` is
 * an auto-incrementing PK, so ascending id is exactly chronological order —
 * matches the mock fixtures' oldest-first convention, whereas `audit.list`'s
 * own wire order is newest-first for its own paging purposes). Never throws
 * for "no matches" — an empty, non-truncated result is a real, valid outcome
 * (see `resolvePayRunAuditState`'s `empty` state); it only throws if
 * `deps.listAudit` itself rejects (network/auth failure), which the caller
 * (`use_pay_run_audit.ts`) catches and classifies via
 * {@link classifyAuditListFailure}.
 *
 * `payRunCreatedAt` (the target pay run's own `PayRun.created_at`, an ISO
 * instant) powers the early-exit optimization described in
 * {@link AUDIT_LIST_MAX_PAGES}'s doc comment: no real audit entry FOR this
 * pay run can have a `created_at` earlier than the run's own creation, so
 * once an entire page's entries are all older than `payRunCreatedAt`, every
 * subsequent (still-older) page is provably irrelevant and the search stops
 * WITHOUT marking the result `truncated` — this is a confirmed-complete
 * search, not a truncated one, even though it stopped before `hasMore`
 * turned `false` on the wire.
 *
 * De-dupes by `id` across pages (a `Map` keyed on `id`, last-write-wins,
 * though every write for a given id is identical anyway) — `audit.list`
 * paginates by `ORDER BY created_at DESC LIMIT/OFFSET` with no tiebreaker
 * and no cursor (`apps/api/src/trpc/routers/audit.ts`), so a same-timestamp
 * tie (routine — `logAuditEntry` timestamps with `new Date().toISOString()`
 * inside a single Worker invocation, and Workers can freeze the clock
 * between I/O so several rows written in one request share an identical
 * value) sitting exactly on a page boundary can appear on both the page
 * that "should" hold it and the next one, or (rarer) be skipped if a
 * concurrent insert shifts the window between two page fetches. De-duping
 * makes the double-counted case exactly right; it cannot recover a row the
 * shifted window skipped entirely — that is a real, documented limitation
 * of filtering an entity-wide, offset-paginated feed for one document's
 * rows client-side (see {@link AUDIT_LIST_MAX_PAGES}'s doc comment; tracked
 * as a follow-up to give this drawer its own narrow, cursor-stable
 * server-side procedure).
 */
export async function fetchPayRunAuditRows(
  deps: AuditListDeps,
  context: { firm_id: string; entity_id: string },
  documentType: string,
  documentId: string,
  payRunCreatedAt: string,
): Promise<FetchPayRunAuditRowsResult> {
  const matchesById = new Map<number, RealAuditEntryRow>();
  let offset = 0;
  let truncated = false;

  for (let page = 0; page < AUDIT_LIST_MAX_PAGES; page++) {
    // Sequential, not `Promise.all` -- each page's `offset` depends on the
    // previous page's result, and this loop is bounded by
    // AUDIT_LIST_MAX_PAGES (never unbounded).
    const { entries, hasMore } = await deps.listAudit({
      firm_id: context.firm_id,
      entity_id: context.entity_id,
      limit: AUDIT_LIST_PAGE_SIZE,
      offset,
    });

    // The oldest `created_at` on THIS page -- `audit.list` orders
    // newest-first, but computed via a min-reduce (not "last element")
    // so this stays correct even if that ordering guarantee is ever
    // loosened server-side.
    let oldestInPage: string | undefined;
    for (const entry of entries) {
      if (oldestInPage === undefined || entry.created_at < oldestInPage) {
        oldestInPage = entry.created_at;
      }
      if (
        entry.entity_type === documentType &&
        entry.entity_ref_id === documentId
      ) {
        matchesById.set(entry.id, {
          id: entry.id,
          action: entry.action,
          actor_id: entry.actor_id,
          actor_display_name: entry.actor_display_name ?? undefined,
          created_at: entry.created_at,
        });
      }
    }

    // Early exit: this page's oldest entry already predates the pay run's
    // own creation, so nothing on any further (still-older) page can
    // belong to this run. Confirmed-complete, NOT truncated.
    const provablyExhausted =
      oldestInPage !== undefined && oldestInPage < payRunCreatedAt;
    if (provablyExhausted) break;

    if (!hasMore) break;

    if (page === AUDIT_LIST_MAX_PAGES - 1) {
      // This was the last page the cap allows, `audit.list` still has more,
      // and the early exit above never fired -- genuinely cannot confirm
      // whether this run's entries (or the absence of any) lie beyond the
      // window just searched.
      truncated = true;
      break;
    }
    offset += AUDIT_LIST_PAGE_SIZE;
  }

  return {
    rows: [...matchesById.values()].sort((a, b) => a.id - b.id),
    truncated,
  };
}

/** The `document_type` a pay-run-scoped audit query uses — matches `entity_type: 'pay_run'` in `logAuditEntry` calls (`apps/api/src/trpc/routers/payroll.ts`). Exported so `use_pay_run_audit.ts` never hardcodes the literal a second time. */
export const PAY_RUN_DOCUMENT_TYPE = 'pay_run';
